import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { examAPI } from '../lib/api';
import { 
  Search, ArrowRight, Clock, Target, FileText, TrendingUp, Star, Zap,
  Cpu, Heart, FlaskConical, Leaf, Building2, Banknote, Briefcase, Shield, Scale, BookOpen,
  GraduationCap, ChevronRight, Award, Users, Lock
} from 'lucide-react';
import { getExamIcon } from '../lib/examIcons';
import { toast } from 'sonner';
import LoadingSpinner from '../components/LoadingSpinner';

// Category colors for styling
const getCategoryStyle = (category) => {
  const styles = {
    engineering: { bg: 'bg-blue-500/10', text: 'text-blue-500', gradient: 'from-blue-500 to-cyan-500' },
    medical: { bg: 'bg-rose-500/10', text: 'text-rose-500', gradient: 'from-rose-500 to-pink-500' },
    government: { bg: 'bg-amber-500/10', text: 'text-amber-500', gradient: 'from-amber-500 to-orange-500' },
    banking: { bg: 'bg-emerald-500/10', text: 'text-emerald-500', gradient: 'from-emerald-500 to-teal-500' },
    management: { bg: 'bg-violet-500/10', text: 'text-violet-500', gradient: 'from-violet-500 to-purple-500' },
    defence: { bg: 'bg-slate-500/10', text: 'text-slate-500', gradient: 'from-slate-500 to-gray-500' },
    law: { bg: 'bg-indigo-500/10', text: 'text-indigo-500', gradient: 'from-indigo-500 to-blue-500' },
    science: { bg: 'bg-cyan-500/10', text: 'text-cyan-500', gradient: 'from-cyan-500 to-teal-500' },
  };
  return styles[category] || styles.engineering;
};

const Exams = () => {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    loadExams();
  }, []);

  const loadExams = async () => {
    try {
      const response = await examAPI.getAll();
      setExams(response.data);
    } catch (error) {
      toast.error('Failed to load exams');
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', label: 'All', Icon: BookOpen },
    { id: 'engineering', label: 'Engineering', Icon: Cpu },
    { id: 'medical', label: 'Medical', Icon: Heart },
    { id: 'government', label: 'Government', Icon: Building2 },
    { id: 'banking', label: 'Banking', Icon: Banknote },
    { id: 'management', label: 'Management', Icon: Briefcase },
    { id: 'defence', label: 'Defence', Icon: Shield },
    { id: 'law', label: 'Law', Icon: Scale },
    { id: 'science', label: 'Science', Icon: FlaskConical },
  ];

  const filteredExams = exams.filter((exam) => {
    const matchesSearch = exam.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      exam.full_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || exam.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Separate available and coming soon exams
  const availableExams = filteredExams.filter(exam => exam.is_available);
  const comingSoonExams = filteredExams.filter(exam => !exam.is_available);

  const handleExamSelect = (exam) => {
    if (exam.is_available) {
      navigate(`/exam/${exam.id}`);
    } else {
      toast.info(`${exam.name} is coming soon! We're working hard to add this exam.`, {
        description: 'Stay tuned for updates.',
        duration: 3000
      });
    }
  };

  const handleQuickPractice = (exam, e) => {
    e.stopPropagation();
    if (exam.is_available) {
      navigate(`/practice/${exam.id}`);
    } else {
      toast.info(`Practice for ${exam.name} is coming soon.`, {
        duration: 3000
      });
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground mb-2">Competitive Exams</h1>
          <p className="text-muted-foreground">
            Complete information on syllabus, exam patterns, eligibility & career opportunities
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            type="text"
            placeholder="Search exams..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-11"
          />
        </div>

        {/* Category Filters */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-6">
          {categories.map((cat) => {
            const IconComponent = cat.Icon;
            const isSelected = selectedCategory === cat.id;
            const count = cat.id === 'all' ? exams.length : exams.filter(e => e.category === cat.id).length;
            
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  isSelected
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <IconComponent size={16} />
                {cat.label}
                {count > 0 && (
                  <span className={`text-xs ${isSelected ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                    ({count})
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Results Count */}
        {!loading && filteredExams.length > 0 && (
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              Showing <span className="font-semibold text-foreground">{filteredExams.length}</span> exam{filteredExams.length !== 1 ? 's' : ''}
              {availableExams.length > 0 && (
                <span className="ml-1">
                  (<span className="text-primary font-semibold">{availableExams.length}</span> available)
                </span>
              )}
            </p>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <LoadingSpinner text="Loading exams..." />
        ) : filteredExams.length === 0 ? (
          <Card className="p-12 text-center">
            <Search className="text-muted-foreground mx-auto mb-4" size={32} />
            <h3 className="text-lg font-semibold text-foreground mb-2">No exams found</h3>
            <p className="text-muted-foreground mb-4">Try a different search term</p>
            <Button variant="outline" onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}>
              Clear Filters
            </Button>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Available Exams Section */}
            {availableExams.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Zap className="text-primary" size={18} />
                  </div>
                  <h2 className="text-lg font-bold text-foreground">Available Now</h2>
                  <Badge className="bg-primary/10 text-primary border-0">Start Practicing</Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {availableExams.map((exam) => {
                    const IconComponent = getExamIcon(exam.name);
                    const catStyle = getCategoryStyle(exam.category);
                    
                    return (
                      <Card
                        key={exam.id}
                        onClick={() => handleExamSelect(exam)}
                        className="group relative overflow-hidden border-2 hover:border-primary/50 transition-all duration-300 cursor-pointer hover:shadow-lg"
                      >
                        {/* Top accent bar */}
                        <div className={`h-1 bg-gradient-to-r ${catStyle.gradient}`} />
                        
                        <div className="p-5">
                          {/* Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div className={`w-12 h-12 ${catStyle.bg} rounded-xl flex items-center justify-center`}>
                              <IconComponent className={catStyle.text} size={24} />
                            </div>
                            <Badge className={`${catStyle.bg} ${catStyle.text} border-0 text-xs`}>
                              {exam.category?.charAt(0).toUpperCase() + exam.category?.slice(1) || 'General'}
                            </Badge>
                          </div>

                          {/* Exam Name */}
                          <h3 className="text-lg font-bold text-foreground mb-1 group-hover:text-primary transition-colors">
                            {exam.name}
                          </h3>

                          {/* Full name */}
                          {exam.full_name && (
                            <p className="text-xs text-muted-foreground mb-3 line-clamp-1">
                              {exam.full_name}
                            </p>
                          )}

                          {/* Description */}
                          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                            {exam.description || 'Study materials and practice questions'}
                          </p>

                          {/* Stats Row */}
                          <div className="grid grid-cols-3 gap-2 p-3 bg-muted/50 rounded-lg mb-4">
                            <div className="text-center">
                              <div className="flex items-center justify-center mb-1">
                                <FileText size={14} className="text-primary" />
                              </div>
                              <p className="text-sm font-bold text-foreground">{exam.total_questions || '—'}</p>
                              <p className="text-[10px] text-muted-foreground">Questions</p>
                            </div>
                            <div className="text-center border-x border-border">
                              <div className="flex items-center justify-center mb-1">
                                <Clock size={14} className="text-amber-500" />
                              </div>
                              <p className="text-sm font-bold text-foreground">{exam.duration_minutes || '—'}</p>
                              <p className="text-[10px] text-muted-foreground">Minutes</p>
                            </div>
                            <div className="text-center">
                              <div className="flex items-center justify-center mb-1">
                                <Award size={14} className="text-emerald-500" />
                              </div>
                              <p className="text-sm font-bold text-foreground">{exam.total_marks || '—'}</p>
                              <p className="text-[10px] text-muted-foreground">Marks</p>
                            </div>
                          </div>

                          {/* CTA */}
                          <div className="flex gap-2">
                            <Button 
                              className="flex-1 h-9"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExamSelect(exam);
                              }}
                            >
                              View Details
                              <ArrowRight size={16} className="ml-2" />
                            </Button>
                            <Button
                              variant="outline"
                              className="h-9 w-9 p-0"
                              onClick={(e) => handleQuickPractice(exam, e)}
                              title="Quick Practice"
                            >
                              <Zap size={16} />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Coming Soon Exams Section */}
            {comingSoonExams.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-muted rounded-lg flex items-center justify-center">
                    <Clock className="text-muted-foreground" size={18} />
                  </div>
                  <h2 className="text-lg font-bold text-foreground">Coming Soon</h2>
                  <Badge variant="secondary" className="text-xs">More exams on the way</Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {comingSoonExams.map((exam) => {
                    const IconComponent = getExamIcon(exam.name);
                    const catStyle = getCategoryStyle(exam.category);
                    
                    return (
                      <Card
                        key={exam.id}
                        onClick={() => handleExamSelect(exam)}
                        className="group relative overflow-hidden border-2 border-dashed opacity-75 hover:opacity-100 transition-all duration-300 cursor-pointer"
                      >
                        {/* Coming Soon Overlay Badge */}
                        <div className="absolute top-3 right-3 z-10">
                          <Badge className="bg-amber-500/90 text-white border-0 text-xs flex items-center gap-1">
                            <Lock size={10} />
                            Coming Soon
                          </Badge>
                        </div>
                        
                        {/* Top accent bar - muted */}
                        <div className={`h-1 bg-gradient-to-r ${catStyle.gradient} opacity-50`} />
                        
                        <div className="p-5">
                          {/* Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div className={`w-12 h-12 ${catStyle.bg} rounded-xl flex items-center justify-center opacity-70`}>
                              <IconComponent className={catStyle.text} size={24} />
                            </div>
                            <Badge className={`${catStyle.bg} ${catStyle.text} border-0 text-xs opacity-70`}>
                              {exam.category?.charAt(0).toUpperCase() + exam.category?.slice(1) || 'General'}
                            </Badge>
                          </div>

                          {/* Exam Name */}
                          <h3 className="text-lg font-bold text-foreground mb-1">
                            {exam.name}
                          </h3>

                          {/* Full name */}
                          {exam.full_name && (
                            <p className="text-xs text-muted-foreground mb-3 line-clamp-1">
                              {exam.full_name}
                            </p>
                          )}

                          {/* Description */}
                          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                            {exam.description || 'Study materials and practice questions'}
                          </p>

                          {/* Stats Row - Placeholder */}
                          <div className="grid grid-cols-3 gap-2 p-3 bg-muted/30 rounded-lg mb-4">
                            <div className="text-center">
                              <div className="flex items-center justify-center mb-1">
                                <FileText size={14} className="text-muted-foreground" />
                              </div>
                              <p className="text-sm font-bold text-muted-foreground">—</p>
                              <p className="text-[10px] text-muted-foreground">Questions</p>
                            </div>
                            <div className="text-center border-x border-border">
                              <div className="flex items-center justify-center mb-1">
                                <Clock size={14} className="text-muted-foreground" />
                              </div>
                              <p className="text-sm font-bold text-muted-foreground">—</p>
                              <p className="text-[10px] text-muted-foreground">Minutes</p>
                            </div>
                            <div className="text-center">
                              <div className="flex items-center justify-center mb-1">
                                <Award size={14} className="text-muted-foreground" />
                              </div>
                              <p className="text-sm font-bold text-muted-foreground">—</p>
                              <p className="text-[10px] text-muted-foreground">Marks</p>
                            </div>
                          </div>

                          {/* CTA - Notify Me */}
                          <Button 
                            variant="outline"
                            className="w-full h-9"
                            onClick={(e) => {
                              e.stopPropagation();
                              toast.success(`We'll notify you when ${exam.name} is available.`, {
                                description: 'Thanks for your interest.',
                                duration: 3000
                              });
                            }}
                          >
                            <Clock size={16} className="mr-2" />
                            Notify Me
                          </Button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Exams;
